import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { SkeletonUtils } from 'three-stdlib'
import { cloneAndScaleUV, setupTextureRepeat } from "../../utils/geometryUltis"
import useGateBoundingDataChecker from "../../utils/gateBoundingDataChecker"
import DoorSingle from '../Models/Door-Single'

// ✅ shader: wycina "dziurę" w materiale bramy (bez ograniczenia po X)
const attachDoorCutoutShader = (mat) => {
  mat.userData.doorCutout = mat.userData.doorCutout || {
    count: 0,
    centers: [new THREE.Vector3(), new THREE.Vector3()],
    half: [new THREE.Vector2(), new THREE.Vector2()],
  }

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDoorCount = { value: mat.userData.doorCutout.count }
    shader.uniforms.uDoorCenter0 = { value: mat.userData.doorCutout.centers[0] }
    shader.uniforms.uDoorCenter1 = { value: mat.userData.doorCutout.centers[1] }
    shader.uniforms.uDoorHalf0 = { value: mat.userData.doorCutout.half[0] }
    shader.uniforms.uDoorHalf1 = { value: mat.userData.doorCutout.half[1] }

    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        void main() {
      `
    )

    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
        #include <begin_vertex>
        vec4 wp = modelMatrix * vec4(transformed, 1.0);
        vWorldPos = wp.xyz;
      `
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        uniform int uDoorCount;
        uniform vec3 uDoorCenter0;
        uniform vec3 uDoorCenter1;
        uniform vec2 uDoorHalf0;
        uniform vec2 uDoorHalf1;

        bool insideRect(vec3 c, vec2 h){
          return (abs(vWorldPos.y - c.y) <= h.x) && (abs(vWorldPos.z - c.z) <= h.y);
        }

        void main() {
          if (uDoorCount > 0) {
            if (insideRect(uDoorCenter0, uDoorHalf0)) discard;
          }
          if (uDoorCount > 1) {
            if (insideRect(uDoorCenter1, uDoorHalf1)) discard;
          }
      `
    )

    mat.userData.shader = shader
  }

  mat.customProgramCacheKey = () => 'swing_gate_door_cutout_v1'
  mat.needsUpdate = true
}

// ✅ shader: wycina "dziurę" tylko w pasie X (np. za bramą)
const attachDoorCutoutShaderXSlab = (mat) => {
  mat.userData.doorCutout = mat.userData.doorCutout || {
    count: 0,
    centers: [new THREE.Vector3(), new THREE.Vector3()],
    half: [new THREE.Vector2(), new THREE.Vector2()],
  }

  mat.userData.xSlab = mat.userData.xSlab || { min: -9999, max: 9999 }

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDoorCount = { value: mat.userData.doorCutout.count }
    shader.uniforms.uDoorCenter0 = { value: mat.userData.doorCutout.centers[0] }
    shader.uniforms.uDoorCenter1 = { value: mat.userData.doorCutout.centers[1] }
    shader.uniforms.uDoorHalf0 = { value: mat.userData.doorCutout.half[0] }
    shader.uniforms.uDoorHalf1 = { value: mat.userData.doorCutout.half[1] }

    shader.uniforms.uXMin = { value: mat.userData.xSlab.min }
    shader.uniforms.uXMax = { value: mat.userData.xSlab.max }

    shader.vertexShader = shader.vertexShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        void main() {
      `
    )

    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
        #include <begin_vertex>
        vec4 wp = modelMatrix * vec4(transformed, 1.0);
        vWorldPos = wp.xyz;
      `
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `
        varying vec3 vWorldPos;
        uniform int uDoorCount;
        uniform vec3 uDoorCenter0;
        uniform vec3 uDoorCenter1;
        uniform vec2 uDoorHalf0;
        uniform vec2 uDoorHalf1;
        uniform float uXMin;
        uniform float uXMax;

        bool insideRect(vec3 c, vec2 h){
          return (abs(vWorldPos.y - c.y) <= h.x) && (abs(vWorldPos.z - c.z) <= h.y);
        }

        void main() {
          if (vWorldPos.x >= uXMin && vWorldPos.x <= uXMax) {
            if (uDoorCount > 0) {
              if (insideRect(uDoorCenter0, uDoorHalf0)) discard;
            }
            if (uDoorCount > 1) {
              if (insideRect(uDoorCenter1, uDoorHalf1)) discard;
            }
          }
      `
    )

    mat.userData.shader = shader
  }

  mat.customProgramCacheKey = () => 'swing_gate_door_cutout_xslab_v1'
  mat.needsUpdate = true
}

const BramaUchylna = forwardRef(({ gate, onUpdate, textures, doorsData, ...props }, ref) => {
  const wrapperRef = useRef()
  const gateRef = useRef()

  const { scene, animations } = useGLTF('/models/gates/Brama-uchylna-v4.glb')
  const cloned = useRef()
  // ✅ panel bramy: cutout bez slab
  const cutoutMaterialsRef = useRef([])
  // ✅ elementy "za bramą": cutout ze slabem
  const reinforcementCutMatsRef = useRef([])

  const [scaledAnimations, setScaledAnimations] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  const EDGE_OFFSET_M = 0.2
  // 👉 tutaj zmień na 0.3 jeśli chcesz płycej
  const REINFORCEMENT_DEPTH_X = 0.6

  const assignedDoors = useMemo(() => {
    const list = doorsData?.front_wall || []
    return list.filter((d) => d?.gateId === gate?.id)
  }, [doorsData, gate?.id])

  const hasDoorsInGate = assignedDoors.length > 0

  useImperativeHandle(ref, () => gateRef.current)

  const freezeClosedPose = useCallback(() => {
    if (!mixerRef.current) return
    if (!gateRef.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return

    const mixer = mixerRef.current
    mixer.stopAllAction()

    const clip = scaledAnimations[0]
    const action = mixer.clipAction(clip)
    action.reset()
    action.enabled = true
    action.clampWhenFinished = true
    action.setLoop(THREE.LoopOnce, 1)
    action.time = 0
    action.timeScale = 0
    action.paused = false
    action.play()
    action.paused = true

    mixer.update(0)

    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [scaledAnimations])

  // ======================================================================
  // CLONE + UV + MATERIALS
  // - panel bramy ("Material") => kolor + cutout
  // - obramowanie/metal => nie dotykamy
  // - reszta za bramą => x-slab cutout (bez zmiany koloru)
  // ======================================================================
  useEffect(() => {
    if (!scene) return
    if (!gateRef.current) return

    const newClone = SkeletonUtils.clone(scene)

    cutoutMaterialsRef.current = []
    reinforcementCutMatsRef.current = []

    const bone = newClone.getObjectByName('Bone003')
    if (bone) {
      bone.traverse((child) => {
        if (!child.isMesh || !child.geometry) return

        // UV scale
        child.geometry = cloneAndScaleUV(
          child.geometry.clone(),
          1,
          1,
          1,
          gate.width / 100,
          gate.height / 100
        )

        // klonuj materiał żeby nie mutować globalnego
        if (child.material) child.material = child.material.clone()

        const meshName = String(child.name || '').toLowerCase()
        const matName = String(child.material?.name || '').toLowerCase()

        // ✅ panel bramy
        if (child.material?.name === 'Material') {
          if (!textures || !textures.gates_swing_color) return

          const gatesColor = textures.gates_swing_color
          const mat = child.material.clone()

          mat.map = gatesColor.textureDiff ?? null
          mat.metalnessMap = gatesColor.textureMetalness ?? null
          mat.normalMap = gatesColor.textureNormalness ?? null

          if (mat.map) setupTextureRepeat(mat.map, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical)
          if (mat.metalnessMap) setupTextureRepeat(mat.metalnessMap, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical)
          if (mat.normalMap) setupTextureRepeat(mat.normalMap, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical)

          mat.metalness = 1
          mat.roughness = 0.5
          mat.color = new THREE.Color(gatesColor.hex)
          mat.needsUpdate = true

          attachDoorCutoutShader(mat)
          cutoutMaterialsRef.current.push(mat)
          child.material = mat
          return
        }

        // ✅ obramowanie/metal – nie ruszamy shaderem (żeby nie było artefaktów)
        const isFrameLike =
          matName.includes('metal') ||
          matName.includes('dark') ||
          matName.includes('frame') ||
          meshName.includes('frame') ||
          meshName.includes('obram') ||
          meshName.includes('ram') ||
          meshName.includes('border')

        if (isFrameLike) return

        // ✅ cała reszta "za bramą" – dajemy x-slab cutout, bez zmiany tekstur/kolorów
        if (child.material) {
          const mat = child.material.clone()
          attachDoorCutoutShaderXSlab(mat)
          reinforcementCutMatsRef.current.push(mat)
          child.material = mat
        }
      })
    }

    cloned.current = newClone
    gateRef.current.clear()
    gateRef.current.add(newClone)
  }, [scene, gate.width, gate.height, props.length, props.width, textures])

  // Extract nodes (do gateBoundingDataChecker)
  const { nodes } = useMemo(() => {
    const nodes = {}
    if (cloned.current) {
      cloned.current.traverse((obj) => {
        if (obj.name) nodes[obj.name] = obj
      })
    }
    return { nodes }
  }, [cloned.current])

  // Clone animations
  useEffect(() => {
    if (!gateRef.current) return
    if (!animations) return

    const newScaledAnimations = animations.map((clip) => {
      const clonedTracks = clip.tracks.map((track) => {
        if (track.name.endsWith(".position")) {
          return new THREE.VectorKeyframeTrack(
            track.name,
            Array.from(track.times),
            Array.from(track.values)
          )
        }
        return track.clone()
      })
      return new THREE.AnimationClip(clip.name, -1, clonedTracks)
    })

    setScaledAnimations(newScaledAnimations)
  }, [animations, gate.width, gate.height, gate.position, props.width, props.length, textures])

  // Mixer setup
  useEffect(() => {
    if (!gateRef.current) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(gateRef.current)
    }

    mixerRef.current = new THREE.AnimationMixer(gateRef.current)

    scaledAnimations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
      action.timeScale = 0
      action.time = 0
      action.play()
      action.paused = true
    })

    if (scaledAnimations.length > 0) freezeClosedPose()
  }, [scaledAnimations, freezeClosedPose])

  // Animation loop
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // Jeśli drzwi przypisane -> zamroź i blokuj animacje
  useEffect(() => {
    if (!hasDoorsInGate) return
    freezeClosedPose()
  }, [hasDoorsInGate, freezeClosedPose])

  // Reset animation when position changes
  useEffect(() => {
    if (!mixerRef.current) return
    scaledAnimations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      action.reset()
      action.stop()
    })
    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [gate.position, scaledAnimations])

  // ==========================================================
  // doorRects (WORLD Y/Z) + update uniformów
  // gateSide: 'left' | 'right'
  // ==========================================================
  const doorRects = useMemo(() => {
    const list = (assignedDoors || []).slice(0, 2)
    const gateWm = (gate.width || 0) / 100
    const gateWorldZ = gate.position?.z ?? 0

    return list.map((d) => {
      const side = String(d?.gateSide || 'left').toLowerCase() === 'right' ? 2 : 1
      const doorWm = (Number(d.width || d.size || 0) || 0) / 100
      const doorHm = (Number(d.height || 0) || 0) / 100

      const centerOffset = Math.max(0, gateWm / 2 - EDGE_OFFSET_M - doorWm / 2)
      const centerZ = gateWorldZ + (side === 2 ? +centerOffset : -centerOffset)
      const centerY = doorHm / 2

      return {
        center: new THREE.Vector3(0, centerY, centerZ),
        half: new THREE.Vector2(doorHm / 2, doorWm / 2),
      }
    })
  }, [assignedDoors, gate.width, gate.position?.z])

  const applyDoorRectsToMaterial = (mat, rects) => {
    const cut = mat?.userData?.doorCutout
    if (!cut) return

    cut.count = rects.length
    cut.centers[0].set(0, -9999, -9999)
    cut.centers[1].set(0, -9999, -9999)
    cut.half[0].set(0, 0)
    cut.half[1].set(0, 0)

    if (rects[0]) { cut.centers[0].copy(rects[0].center); cut.half[0].copy(rects[0].half) }
    if (rects[1]) { cut.centers[1].copy(rects[1].center); cut.half[1].copy(rects[1].half) }

    const sh = mat.userData.shader
    if (sh?.uniforms) {
      sh.uniforms.uDoorCount.value = cut.count
      sh.uniforms.uDoorCenter0.value.copy(cut.centers[0])
      sh.uniforms.uDoorCenter1.value.copy(cut.centers[1])
      sh.uniforms.uDoorHalf0.value.copy(cut.half[0])
      sh.uniforms.uDoorHalf1.value.copy(cut.half[1])
    }

    mat.needsUpdate = true
  }

  // ✅ update panel cutout uniforms
  useEffect(() => {
    const mats = cutoutMaterialsRef.current || []
    if (!mats.length) return
    mats.forEach((mat) => applyDoorRectsToMaterial(mat, doorRects))
  }, [doorRects])

  // ✅ update x-slab cutout uniforms (za bramą)
  useEffect(() => {
    const mats = reinforcementCutMatsRef.current || []
    if (!mats.length) return

    const xMin = gate.position.x
    const xMax = gate.position.x + REINFORCEMENT_DEPTH_X

    mats.forEach((mat) => {
      applyDoorRectsToMaterial(mat, doorRects)

      mat.userData.xSlab.min = xMin
      mat.userData.xSlab.max = xMax

      const sh = mat.userData.shader
      if (sh?.uniforms) {
        sh.uniforms.uXMin.value = xMin
        sh.uniforms.uXMax.value = xMax
      }

      mat.needsUpdate = true
    })
  }, [doorRects, gate.position.x])

  // Bounding box updater (tylko brama)
  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: () => {
      if (isAnimatingRef.current) return
      if (!gateRef.current) return

      const box = new THREE.Box3().setFromObject(gateRef.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = { boundingData: { box, size } }

      if (!isOpen) {
        const [x = 0, y = 0, z = 0] = [(-props.length / 100) / 2 + 0.5, 0, gate.position.z]
        const adjustedY = y + gate.height / 100 / 2

        updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
        updateData.positionAdjusted = [x, adjustedY, z]
        onUpdate(updateData)
      }
    },
    group: gateRef,
  })

  // Click to open/close
  const handleClick = () => {
    if (hasDoorsInGate) return
    if (!mixerRef.current || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)
    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpen) {
      action.time = 0
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    setIsOpen(!isOpen)
  }

  // Pozycja drzwi w bramie (dla DoorSingle front_wall)
  const computeDoorPositionForDoorSingle = useCallback((door) => {
    const side = String(door?.gateSide || 'left').toLowerCase() === 'right' ? 2 : 1

    const gateWm = (gate.width || 0) / 100
    const doorWm = (Number(door.width || door.size || 0) || 0) / 100

    const centerOffset = Math.max(0, gateWm / 2 - EDGE_OFFSET_M - doorWm / 2)
    const targetWorldZ = gate.position.z + (side === 2 ? +centerOffset : -centerOffset)

    // jeśli będzie z-fight, zmień 0.01
    const targetWorldX = gate.position.x + 0.01 - 0.45
    const targetWorldY = 0

    return { x: targetWorldX, y: targetWorldY, z: -targetWorldZ }
  }, [gate.width, gate.position.x, gate.position.z])

  return (
    <group ref={wrapperRef} dispose={null}>
      <group
        ref={gateRef}
        wall={'front_wall'}
        dispose={null}
        position={[gate.position.x, gate.position.y, gate.position.z]}
        castShadow={true}
        scale={[gate.height / 100, gate.height / 100, gate.width / 100]}
        onClick={handleClick}
      />

      {assignedDoors.map((door, idx) => {
        const pos = computeDoorPositionForDoorSingle(door)
        const doorForRender = { ...door, position: pos, wall: 'front_wall' }

        return (
          <DoorSingle
            key={`swing-gate-${gate.id}-door-${doorForRender.id ?? idx}`}
            door={doorForRender}
            wall={'front_wall'}
            onUpdate={() => {}}
            textures={textures}
            disableAnimation={true}
            gateTexture={textures?.gates_swing_color}
            {...props}
          />
        )
      })}
    </group>
  )
})

export default BramaUchylna
useGLTF.preload('/models/gates/Brama-uchylna-v4.glb')
